import { store } from '../redux/store';
import { loginWithApple, registerWithApple } from '../redux/auth/authOperations';
import { Notify } from 'notiflix/build/notiflix-notify-aio';
import i18n from '../i18n';

// Apple OAuth Configuration
const APPLE_CLIENT_ID =
  process.env.REACT_APP_APPLE_CLIENT_ID ||
  'com.netmarketolog.fintrack.service';

const missingAccountKeywords = [
  'not found',
  'no user',
  'does not exist',
  'please register',
  'register first',
  'account removed',
  'deleted',
];

const extractErrorMessage = (error) => {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (error.response?.data?.message) return String(error.response.data.message);
  if (error.response?.data?.error) return String(error.response.data.error);
  if (error.message) return String(error.message);
  return undefined;
};

const isMissingAccountError = (message) => {
  if (!message) return false;
  const lowered = message.toLowerCase();
  return missingAccountKeywords.some((keyword) => lowered.includes(keyword));
};

const redirectToRegister = () => {
  Notify.failure(i18n.t('auth.user_not_found'));
  Notify.info(i18n.t('auth.please_register_first'));
  setTimeout(() => {
    window.location.href = '/#/';
  }, 1500);
};

class AppleAuthService {
  constructor() {
    this.isAppleLoaded = false;
    this.isRegistrationMode = false;
  }

  // Загружаем Apple JS SDK
  async loadAppleScript() {
    if (this.isAppleLoaded) return;

    return new Promise((resolve, reject) => {
      // Устанавливаем meta теги для Apple Sign In
      const redirectURI = window.location.origin + '/oauth/apple/callback';
      
      // Проверяем и создаем meta теги
      let clientIdMeta = document.querySelector('meta[name="appleid-signin-client-id"]');
      if (!clientIdMeta) {
        clientIdMeta = document.createElement('meta');
        clientIdMeta.name = 'appleid-signin-client-id';
        clientIdMeta.content = APPLE_CLIENT_ID;
        document.head.appendChild(clientIdMeta);
      } else {
        clientIdMeta.content = APPLE_CLIENT_ID;
      }

      let redirectMeta = document.querySelector('meta[name="appleid-signin-redirect-uri"]');
      if (!redirectMeta) {
        redirectMeta = document.createElement('meta');
        redirectMeta.name = 'appleid-signin-redirect-uri';
        redirectMeta.content = redirectURI;
        document.head.appendChild(redirectMeta);
      } else {
        redirectMeta.content = redirectURI;
      }

      let scopeMeta = document.querySelector('meta[name="appleid-signin-scope"]');
      if (!scopeMeta) {
        scopeMeta = document.createElement('meta');
        scopeMeta.name = 'appleid-signin-scope';
        scopeMeta.content = 'name email';
        document.head.appendChild(scopeMeta);
      }

      const script = document.createElement('script');
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isAppleLoaded = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Apple Sign In script'));
      };

      document.head.appendChild(script);
    });
  }

  // Инициализация Apple Sign In
  async initialize() {
    try {
      await this.loadAppleScript();

      // Ждем, пока AppleID будет доступен
      let attempts = 0;
      const maxAttempts = 10;
      while (typeof window === 'undefined' || !window.AppleID) {
        if (attempts >= maxAttempts) {
          throw new Error('AppleID SDK not loaded');
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      // Инициализация уже происходит через meta теги в index.html
      // Но можно также инициализировать программно
      if (window.AppleID && window.AppleID.auth) {
        // Проверяем, не инициализирован ли уже
        if (!window.AppleID.auth._initialized) {
          window.AppleID.auth.init({
            clientId: APPLE_CLIENT_ID,
            scope: 'name email',
            redirectURI: window.location.origin + '/oauth/apple/callback',
            usePopup: true,
          });
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // Обработка ответа от Apple Sign In
  async handleAppleResponse(response) {
    try {
      const { authorization } = response;

      if (!authorization) {
        throw new Error('No authorization received from Apple');
      }

      const { id_token: identityToken } = authorization;

      if (!identityToken) {
        throw new Error('No identity token received from Apple');
      }

      // Получаем email и fullName из response (если доступны)
      const email = response.user?.email;
      const fullName = response.user?.name;

      // Используем состояние класса для определения режима (логин/регистрация)
      const isRegistration = this.isRegistrationMode;

      // Отправляем identity token на бэкенд для аутентификации через Redux
      const action = isRegistration
        ? registerWithApple({ identityToken, email, fullName })
        : loginWithApple({ identityToken, email, fullName });

      const result = await store.dispatch(action);

      if (result.type.endsWith('/fulfilled')) {
        Notify.success(
          isRegistration
            ? i18n.t('oauth.appleRegisterSuccess')
            : i18n.t('oauth.appleSignInSuccess')
        );

        // Отправляем кастомное событие для уведомления о успешной аутентификации
        window.dispatchEvent(new Event('appleAuthSuccess'));
      } else {
        throw new Error('No authentication token received');
      }
    } catch (error) {
      const message = extractErrorMessage(error);
      const isRegistration = this.isRegistrationMode;
      if (!isRegistration && isMissingAccountError(message)) {
        redirectToRegister();
      } else {
        Notify.failure(
          message ||
            (isRegistration
              ? i18n.t('oauth.appleRegisterFailed')
              : i18n.t('oauth.appleSignInFailed'))
        );
      }
    }
  }

  // Запуск процесса входа через Apple
  async signIn() {
    try {
      this.isRegistrationMode = false;
      await this.initialize();

      if (typeof window !== 'undefined' && window.AppleID) {
        window.AppleID.auth.signIn({
          requestedScopes: ['name', 'email'],
        }).then((response) => {
          this.handleAppleResponse(response);
        }).catch((error) => {
          if (error.error === 'popup_closed_by_user') {
            // Пользователь закрыл окно - не показываем ошибку
            return;
          } else if (error.error === 'invalid_client') {
            Notify.failure(i18n.t('oauth.appleInvalidClient'));
            console.error('Apple Sign In configuration error:', error);
          } else {
            Notify.failure(
              i18n.t('oauth.appleSignInFailedWithReason', {
                reason: error.error || error.message || 'Unknown error',
              })
            );
            console.error('Apple Sign In error:', error);
          }
        });
      } else {
        // Fallback: используем redirect
        this.showAppleRedirect(false);
      }
    } catch (error) {
      console.error('Apple Sign In initialization error:', error);
      Notify.failure(i18n.t('oauth.appleInitSignInFailed'));
    }
  }

  // Fallback: используем redirect для входа
  showAppleRedirect(isRegistration = false) {
    const redirectUri = encodeURIComponent(
      window.location.origin + '/oauth/apple/callback'
    );
    const state = isRegistration ? 'register' : 'login';
    const nonce = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    // Используем response_mode=form_post для получения данных через POST
    // или response_mode=fragment для получения через URL fragment
    const redirectUrl = `https://appleid.apple.com/auth/authorize?client_id=${APPLE_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=id_token&scope=name%20email&response_mode=form_post&state=${state}&nonce=${nonce}`;

    window.location.href = redirectUrl;
  }

  // Запуск процесса регистрации через Apple
  async signUp() {
    try {
      this.isRegistrationMode = true;
      await this.initialize();

      if (typeof window !== 'undefined' && window.AppleID) {
        window.AppleID.auth.signIn({
          requestedScopes: ['name', 'email'],
        }).then((response) => {
          this.handleAppleResponse(response);
        }).catch((error) => {
          if (error.error === 'popup_closed_by_user') {
            // Пользователь закрыл окно - не показываем ошибку
            return;
          } else if (error.error === 'invalid_client') {
            Notify.failure(i18n.t('oauth.appleInvalidClient'));
            console.error('Apple Sign In configuration error:', error);
          } else {
            Notify.failure(
              i18n.t('oauth.appleSignUpFailedWithReason', {
                reason: error.error || error.message || 'Unknown error',
              })
            );
            console.error('Apple Sign Up error:', error);
          }
        });
      } else {
        // Fallback: используем redirect
        this.showAppleRedirect(true);
      }
    } catch (error) {
      console.error('Apple Sign Up initialization error:', error);
      Notify.failure(i18n.t('oauth.appleInitSignUpFailed'));
    }
  }

  // Обработка redirect callback от Apple (если используется form_post)
  async handleRedirectCallback() {
    // Apple использует form_post, поэтому данные приходят через POST
    // Это нужно обрабатывать на сервере или через специальный endpoint
    // Для клиентской части используем popup flow
    const urlParams = new URLSearchParams(window.location.search);
    const id_token = urlParams.get('id_token');
    const state = urlParams.get('state');

    if (id_token) {
      await this.handleIdToken(id_token, state);
    }
  }

  // Обработка identity token от Apple
  async handleIdToken(identityToken, state, email = null, fullName = null) {
    try {
      // Отправляем данные в Redux через соответствующий action
      const action = state === 'register'
        ? registerWithApple({ identityToken, email, fullName })
        : loginWithApple({ identityToken, email, fullName });

      const result = await store.dispatch(action);

      if (result.type.endsWith('/fulfilled')) {
        // Показываем успешное сообщение
        Notify.success(
          state === 'register'
            ? i18n.t('oauth.appleRegisterSuccess')
            : i18n.t('oauth.appleSignInSuccess')
        );
      } else {
        throw new Error('Failed to process authentication');
      }
    } catch (error) {
      const message = extractErrorMessage(error);

      // При регистрации не редиректим на регистрацию при ошибке "user not found"
      if (state === 'register') {
        Notify.failure(message || i18n.t('oauth.appleRegisterFailed'));
      } else if (isMissingAccountError(message)) {
        // При логине редиректим на регистрацию, если пользователь не найден
        redirectToRegister();
      } else {
        Notify.failure(message || i18n.t('oauth.appleProcessAuthFailed'));
      }

      // Очищаем URL от параметров OAuth
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  // Выход из Apple аккаунта
  async signOut() {
    try {
      Notify.success(i18n.t('oauth.appleSignOutSuccess'));
    } catch (error) {
      Notify.failure(i18n.t('oauth.appleSignOutFailed'));
    }
  }
}

export const appleAuthService = new AppleAuthService();
export default appleAuthService;

